import { useCallback, useState } from "react";
import type { ReactNode } from "react";
import { useAuth } from "./useAuth";
import { useLicense } from "./useLicense";
import { useTermsAcceptance } from "./useTermsAcceptance";
import { getStoredTheme } from "../hooks/getStoredTheme";
import { SignInScreen } from "../screens/SignInScreen";
import { PaymentRequiredScreen } from "../screens/PaymentRequiredScreen";
import { PaymentSuccessScreen } from "../screens/PaymentSuccessScreen";
import { UnlockScreen } from "../screens/UnlockScreen";
import { OnboardingScreen } from "../screens/OnboardingScreen";
import { TermsAcceptanceScreen } from "../screens/TermsAcceptanceScreen";
import { STORAGE_KEYS, APP_URL_PARAMS, SITE_URLS } from "../constants";
import "../styles/monastery.css";

function clearInviteCode(): void {
  try { sessionStorage.removeItem(STORAGE_KEYS.PREFILLED_INVITE_CODE); } catch {}
}

/** Read all URL params of interest in one pass and clear them with a single replaceState call. */
function consumeUrlParams(): {
  hasPaymentRedirect: boolean;
  unlockToken: string | null;
  prefilledInvite: string | null;
} {
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get(APP_URL_PARAMS.SESSION_ID);
  const token = params.get(APP_URL_PARAMS.TOKEN);
  const invite = params.get(APP_URL_PARAMS.INVITE);

  for (const key of [APP_URL_PARAMS.SESSION_ID, APP_URL_PARAMS.TOKEN, APP_URL_PARAMS.INVITE]) params.delete(key);
  const q = params.toString();
  // Preserve the hash so supabase-js can read #access_token= (implicit flow).
  // authContext.ts cleans it up after the session is established.
  const hash = window.location.hash;
  window.history.replaceState(
    {},
    "",
    (q ? `${window.location.pathname}?${q}` : window.location.pathname) + hash,
  );

  // Persist invite through OAuth redirects; fall back to sessionStorage if not in URL.
  let resolvedInvite = invite;
  if (invite) {
    try { sessionStorage.setItem(STORAGE_KEYS.PREFILLED_INVITE_CODE, invite); } catch { /* quota or private browsing */ }
  } else {
    try { resolvedInvite = sessionStorage.getItem(STORAGE_KEYS.PREFILLED_INVITE_CODE); } catch { /* quota or private browsing */ }
  }

  return {
    hasPaymentRedirect: sessionId !== null,
    unlockToken: token,
    prefilledInvite: resolvedInvite,
  };
}

function LoadingShell() {
  return (
    <div className="rc-screen rc-screen--centered">
      <div className="rc-eyebrow" style={{ opacity: 0.4 }}>
        Retreat Conductor
      </div>
    </div>
  );
}

type Props = { children: ReactNode };

export function AuthGate({ children }: Props) {
  const theme = getStoredTheme();
  const { session, loading: authLoading } = useAuth();
  const {
    active,
    loading: licenseLoading,
    error: licenseError,
    refresh,
    redeemToken,
  } = useLicense();

  const [urlInit] = useState(consumeUrlParams);
  const [hasPaymentRedirect, setHasPaymentRedirect] = useState(urlInit.hasPaymentRedirect);
  const [unlockToken, setUnlockToken] = useState(urlInit.unlockToken);
  const prefilledInvite = urlInit.prefilledInvite;

  const [onboardingSeen, setOnboardingSeen] = useState(
    () => localStorage.getItem(STORAGE_KEYS.ONBOARDING_SEEN) === "1",
  );

  const { state: termsState, accept: acceptTerms } = useTermsAcceptance();
  const [isAccepting, setIsAccepting] = useState(false);

  const handleAcceptTerms = useCallback(async () => {
    setIsAccepting(true);
    try {
      await acceptTerms();
    } finally {
      setIsAccepting(false);
    }
  }, [acceptTerms]);

  const handleActivated = useCallback(() => setHasPaymentRedirect(false), []);
  const handleTimeout = useCallback(() => setHasPaymentRedirect(false), []);

  const handleUnlockContinue = useCallback(() => {
    setUnlockToken(null);
    refresh();
  }, [refresh]);

  const handleOnboardingBegin = useCallback(() => {
    localStorage.setItem(STORAGE_KEYS.ONBOARDING_SEEN, "1");
    setOnboardingSeen(true);
  }, []);

  // Determine inner content — single rc-app wrapper, one conditional inner
  let inner: ReactNode;

  if (authLoading) {
    inner = <LoadingShell />;
  } else if (!session) {
    inner = <SignInScreen />;
  } else if (termsState.status === 'loading') {
    inner = <LoadingShell />;
  } else if (termsState.status === 'pending') {
    inner = (
      <TermsAcceptanceScreen
        onAccept={handleAcceptTerms}
        isSubmitting={isAccepting}
        termsUrl={SITE_URLS.TERMS}
      />
    );
  } else if (unlockToken) {
    inner = (
      <UnlockScreen
        token={unlockToken}
        onRedeemToken={redeemToken}
        onContinue={handleUnlockContinue}
      />
    );
  } else if (hasPaymentRedirect) {
    inner = (
      <PaymentSuccessScreen
        onActivated={handleActivated}
        onTimeout={handleTimeout}
      />
    );
  } else if (licenseLoading) {
    inner = <LoadingShell />;
  } else if (licenseError?.class === "system") {
    inner = (
      <div className="rc-screen rc-screen--centered">
        <div className="rc-eyebrow" style={{ opacity: 0.4 }}>
          Something stopped
        </div>
        <p style={{ opacity: 0.6, marginTop: 16, fontSize: 14 }}>
          We couldn't verify your access — please try again.
        </p>
        <button
          onClick={refresh}
          style={{
            marginTop: 24,
            background: "none",
            border: "1px solid var(--rc-ink-3, #999)",
            padding: "10px 24px",
            fontSize: 12,
            cursor: "pointer",
          }}
        >
          Try again
        </button>
      </div>
    );
  } else if (!active) {
    inner = (
      <PaymentRequiredScreen
        initialCode={prefilledInvite ?? undefined}
        onCodeSubmitted={clearInviteCode}
      />
    );
  } else if (!onboardingSeen) {
    inner = <OnboardingScreen onBegin={handleOnboardingBegin} />;
  } else {
    inner = children;
  }

  // Auth-phase screens need the rc-app wrapper (fills viewport with --rc-paper).
  // When inner === children (RetreatBridge), skip it — RetreatBridge has its own wrapper.
  if (inner === children) {
    return <>{children}</>;
  }

  return (
    <div className="rc-app" data-theme={theme}>
      {inner}
    </div>
  );
}
